import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Toast from "@/components/base/Toast";
import VideoPlayer from "@/pages/detail/components/VideoPlayer";
import RelatedList from "@/pages/detail/components/RelatedList";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatCountUp, formatViews, getCategoryBySlug, getRelatedVideos, getVideoById } from "@/lib/video";

export default function DetailPage() {
  const { id = "" } = useParams();
  const video = getVideoById(id);
  const category = video ? getCategoryBySlug(video.category) : undefined;
  const related = useMemo(() => (video ? getRelatedVideos(video, 6) : []), [video]);

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  useDocumentMeta({
    title: video ? `${video.title} - 逼特逼视频` : "视频详情 - 逼特逼视频",
    description: video ? video.description.slice(0, 150) : "观看逼特逼视频的精彩视频内容。",
    keywords: video ? `${video.title},${video.tags.join(",")},逼特逼视频` : "逼特逼视频,视频详情",
  });

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(timer);
  }, [id]);

  const notify = (message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify("链接已复制到剪贴板");
    } catch {
      notify("复制失败，请手动复制地址栏链接");
    }
  };

  if (!video || !category) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-video-off-line"
          title="该视频已下架或不存在"
          description="你访问的视频可能已被移除，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <div className="skeleton-shimmer relative aspect-video w-full overflow-hidden rounded-lg bg-background-200" />
            <div className="skeleton-shimmer relative mt-6 h-6 w-3/4 overflow-hidden rounded bg-background-200" />
            <div className="skeleton-shimmer relative mt-4 h-4 w-1/3 overflow-hidden rounded bg-background-200" />
            <div className="skeleton-shimmer relative mt-6 h-24 w-full overflow-hidden rounded-lg bg-background-200" />
          </div>
          <div className="flex flex-col gap-4">
            <div className="skeleton-shimmer relative h-32 w-full overflow-hidden rounded-lg bg-background-200" />
            <div className="skeleton-shimmer relative h-64 w-full overflow-hidden rounded-lg bg-background-200" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: category.name, to: `/category/${category.slug}` },
          { label: video.title },
        ]}
      />

      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
          <VideoPlayer video={video} onNotify={notify} />

          <h1 className="mt-6 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
            {video.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <Link
              to={`/category/${category.slug}`}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700 transition-colors hover:bg-primary-200"
            >
              <i className={`${category.icon} text-sm`} />
              {category.name}
            </Link>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-play-circle-line text-base" />
              {formatCountUp(video.views)} 次播放
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-thumb-up-line text-base" />
              {formatCountUp(video.likes)} 次点赞
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-calendar-line text-base" />
              {video.uploadDate}
            </span>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-semibold text-foreground-950">视频简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              {video.description}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-600">
                {video.author.slice(0, 1)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground-900">{video.author}</p>
                <p className="mt-0.5 text-xs text-foreground-500">上传于 {video.uploadDate}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-background-100 px-3 py-2 text-center">
                <p className="font-heading text-base font-bold text-foreground-900">
                  {formatViews(video.views)}
                </p>
                <p className="text-xs text-foreground-500">播放量</p>
              </div>
              <div className="rounded-md bg-background-100 px-3 py-2 text-center">
                <p className="font-heading text-base font-bold text-foreground-900">
                  {formatViews(video.likes)}
                </p>
                <p className="text-xs text-foreground-500">点赞量</p>
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-share-line text-base" />
                分享链接
              </button>
              <button
                type="button"
                onClick={() => notify("已加入稍后观看（演示）")}
                className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-bookmark-line text-base" />
                稍后看
              </button>
            </div>
          </div>

          <RelatedList videos={related} />
        </aside>
      </div>

      <Toast message={toast} />
    </div>
  );
}